/**
 * Configuration utility functions
 */

import type { BabylonConfig, PartialBabylonConfig } from "./schemas";

/**
 * Deep merge two objects
 * Arrays are replaced, not merged
 * @param target Target object
 * @param source Source object to merge
 */
export function deepMerge<T extends Record<string, unknown>>(
  target: T,
  source: Partial<T>,
): T {
  const result = { ...target };

  for (const key in source) {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (sourceValue === undefined) {
      continue;
    }

    if (sourceValue === null) {
      result[key] = null as T[Extract<keyof T, string>];
      continue;
    }

    // Replace arrays entirely
    if (Array.isArray(sourceValue)) {
      result[key] = [...sourceValue] as T[Extract<keyof T, string>];
      continue;
    }

    // Deep merge objects
    if (
      typeof sourceValue === "object" &&
      typeof targetValue === "object" &&
      targetValue !== null &&
      !Array.isArray(targetValue)
    ) {
      result[key] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>,
      ) as T[Extract<keyof T, string>];
      continue;
    }

    // Replace primitives and other types
    result[key] = sourceValue as T[Extract<keyof T, string>];
  }

  return result;
}

/**
 * Merge configurations with proper deep merging
 * @param base Base configuration
 * @param override Configuration overrides
 */
export function mergeConfigs(
  base: BabylonConfig,
  override: PartialBabylonConfig,
): BabylonConfig {
  return deepMerge(base, override);
}

/**
 * Create a test configuration builder
 * @param overrides Configuration overrides
 */
export function createTestConfig(
  overrides?: PartialBabylonConfig,
): BabylonConfig {
  const base: BabylonConfig = {
    engine: {
      deterministicLockstep: false,
      lockstepMaxSteps: 4,
      renderHeight: 256,
      renderWidth: 512,
      textureSize: 512,
      type: "null",
    },
    initialEntities: {
      cameras: [],
      lights: [],
      materials: [],
      meshes: [],
    },
    scene: {
      ambientColor: { b: 1, g: 1, r: 1 },
      clearColor: { a: 1, b: 0, g: 0, r: 0 },
      collisionsEnabled: false,
      enableOptimizer: false,
      enablePhysics: false,
      fogMode: "none",
      gravity: { x: 0, y: -9.81, z: 0 },
    },
    sceneManager: {
      autoOptimize: false,
      debug: false,
      enableDebugLayer: false,
      targetFps: 60,
    },
  };

  if (!overrides) {
    return base;
  }

  return mergeConfigs(base, overrides);
}

/**
 * Compare two configurations and return differences
 * @param config1 First configuration
 * @param config2 Second configuration
 */
export function diffConfigs(
  config1: BabylonConfig,
  config2: BabylonConfig,
): ConfigDifference[] {
  const differences: ConfigDifference[] = [];

  const compareValues = (
    path: string,
    value1: unknown,
    value2: unknown,
  ): void => {
    // Handle null/undefined
    if (value1 === value2) {
      return;
    }

    // Handle different types
    if (typeof value1 !== typeof value2) {
      differences.push({
        path,
        type: "type-change",
        value1,
        value2,
      });
      return;
    }

    // Handle arrays
    if (Array.isArray(value1) && Array.isArray(value2)) {
      if (JSON.stringify(value1) !== JSON.stringify(value2)) {
        differences.push({
          path,
          type: "array-change",
          value1,
          value2,
        });
      }
      return;
    }

    // Handle objects
    if (
      typeof value1 === "object" &&
      value1 !== null &&
      typeof value2 === "object" &&
      value2 !== null
    ) {
      const allKeys = new Set([...Object.keys(value1), ...Object.keys(value2)]);

      for (const key of allKeys) {
        compareValues(
          `${path}.${key}`,
          (value1 as Record<string, unknown>)[key],
          (value2 as Record<string, unknown>)[key],
        );
      }
      return;
    }

    // Handle primitives
    if (value1 !== value2) {
      differences.push({
        path,
        type: "value-change",
        value1,
        value2,
      });
    }
  };

  // Start comparison from root
  compareValues("", config1, config2);

  // Remove root path if present
  return differences
    .filter((d) => d.path !== "")
    .map((d) => ({
      ...d,
      path: d.path.startsWith(".") ? d.path.substring(1) : d.path,
    }));
}

/**
 * Configuration difference
 */
export interface ConfigDifference {
  path: string;
  value1: unknown;
  value2: unknown;
  type: "type-change" | "value-change" | "array-change";
}

/**
 * Extract a value from configuration by path
 * @param config Configuration object
 * @param path Dot-separated path
 */
export function getConfigValue(config: BabylonConfig, path: string): unknown {
  const parts = path.split(".");
  let current: unknown = config;

  for (const part of parts) {
    if (typeof current !== "object" || current === null || !(part in current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Set a value in configuration by path
 * @param config Configuration object (will be mutated)
 * @param path Dot-separated path
 * @param value Value to set
 */
export function setConfigValue(
  config: BabylonConfig,
  path: string,
  value: unknown,
): void {
  const parts = path.split(".");
  const lastPart = parts.pop();

  if (!lastPart) {
    throw new Error("Invalid path");
  }

  let current: unknown = config;

  for (const part of parts) {
    if (typeof current !== "object" || current === null) {
      throw new Error(
        `Cannot set value at path ${path}: parent is not an object`,
      );
    }

    if (!(part in current)) {
      (current as Record<string, unknown>)[part] = {};
    }

    current = (current as Record<string, unknown>)[part];
  }

  if (typeof current !== "object" || current === null) {
    throw new Error(
      `Cannot set value at path ${path}: parent is not an object`,
    );
  }

  (current as Record<string, unknown>)[lastPart] = value;
}

/**
 * Clone a configuration deeply
 * @param config Configuration to clone
 */
export function cloneConfig<T extends Record<string, unknown>>(config: T): T {
  return JSON.parse(JSON.stringify(config));
}

/**
 * Check if a configuration has a value at path
 * @param config Configuration object
 * @param path Dot-separated path
 */
export function hasConfigValue(config: BabylonConfig, path: string): boolean {
  const parts = path.split(".");
  let current: unknown = config;

  for (const part of parts) {
    if (typeof current !== "object" || current === null || !(part in current)) {
      return false;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return true;
}

/**
 * Remove a value from configuration by path
 * @param config Configuration object (will be mutated)
 * @param path Dot-separated path
 */
export function removeConfigValue(config: BabylonConfig, path: string): void {
  const parts = path.split(".");
  const lastPart = parts.pop();

  if (!lastPart) {
    throw new Error("Invalid path");
  }

  let current: unknown = config;

  for (const part of parts) {
    if (typeof current !== "object" || current === null || !(part in current)) {
      return; // Path doesn't exist, nothing to remove
    }
    current = (current as Record<string, unknown>)[part];
  }

  if (typeof current === "object" && current !== null) {
    delete (current as Record<string, unknown>)[lastPart];
  }
}

/**
 * Flatten a configuration to a flat key-value object
 * @param config Configuration object
 * @param prefix Path prefix
 */
export function flattenConfig(
  config: Record<string, unknown>,
  prefix = "",
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const key in config) {
    const value = config[key];
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      Object.assign(
        result,
        flattenConfig(value as Record<string, unknown>, fullKey),
      );
    } else {
      result[fullKey] = value;
    }
  }

  return result;
}

/**
 * Unflatten a flat key-value object to a nested configuration
 * @param flat Flat key-value object
 */
export function unflattenConfig(
  flat: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const key in flat) {
    const parts = key.split(".");
    let current = result;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!(part in current)) {
        current[part] = {};
      }
      current = current[part] as Record<string, unknown>;
    }

    const lastPart = parts[parts.length - 1];
    current[lastPart] = flat[key];
  }

  return result;
}
